import React, { memo, useEffect, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { reduxForm } from "redux-form";
import { createStructuredSelector } from "reselect";
// import Alert from "react-s-alert";
import get from "lodash/get";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import SmartRender from "containers/SmartRender";
import {
  forms,
  paths,
  STEP_ACTIONS,
  WEB_CHANNELS,
  OLD_WEBFRONT_URL,
  CONTRACT_TYPES,
  SELF_ACT_DEL
} from "utils/constants";
import validate from "validations";
import {
  makeSelectSimVerificationForm,
  makeSelectSimVerificationFormInitialValues,
  makeSelectPageValidations,
  makeSelectIccIdPrefix,
  makeSelectSimVerificationPayload,
  makeSelectSimActivationPayload,
  makeSelectIccIdValid,
  makeSelectIccIdError,
  makeSelectThirdPartySimVerification,
  makeSelectIccIdDetails,
  makeSelectEligiblePlans,
  makeSelectAppSettings,
  makeSelectValidateIccidStatus,
  makeSelectContractType,
  makeSelectGuestOrder,
  makeSelectMinimumAge,
  makeSelectMonthOptions,
  makeSelectDateOptions,
  makeSelectYearOptions,
  makeSelectIccIdInUrlParam
} from "ducks/selectors";
import {
  validateIccId,
  clearData,
  fetchPlan,
  selectPlan,
  activateSimCard,
  initiatePortIn,
  activateReplaceSimCard,
  setData
} from "ducks/actions";
import { urlParam, gotoUrl, getChannelFromUrl } from "utils/helpers";
import { isWebview } from "utils/localStorage";
import AnalyticsFactory from "analytics/AnalyticsFactory";

const SimVerificationHOC = Component =>
  function Comp(props) {
    const { setIccidParam } = props;
    const icc_id = get(props, "formValues.icc_id");
    const iccidParam = urlParam("iccid");
    const hasIccidParam = Boolean(iccidParam);
    const [manualMode, setManualMode] = useState(!isWebview() || icc_id);
    const [hideInputOptions, setHideInputOptions] = useState(
      iccidParam || urlParam("manual_mode") || icc_id
    );
    const emailInUrl = urlParam("email");

    useEffect(() => {
      AnalyticsFactory.pushProfile("", { user_details: { email: emailInUrl } });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailInUrl]);

    useEffect(() => {
      if (hasIccidParam) setIccidParam(true);
    }, [hasIccidParam, setIccidParam]);

    return (
      <Component
        {...props}
        manualMode={manualMode}
        setManualMode={setManualMode}
        setHideInputOptions={setHideInputOptions}
        hideInputOptions={hideInputOptions}
      />
    );
  };

const mapStateToProps = (_, props) =>
  createStructuredSelector({
    formValues: makeSelectSimVerificationForm(),
    formValidations: makeSelectPageValidations(forms.SIM_VERIFICATION_FORM),
    iccIdPrefix: makeSelectIccIdPrefix(),
    initialValues: makeSelectSimVerificationFormInitialValues(),
    iccIdInUrlParam: makeSelectIccIdInUrlParam(),
    iccIdValid: makeSelectIccIdValid(urlParam("iccid")),
    iccIdError: makeSelectIccIdError(urlParam("iccid")),
    payload: makeSelectSimVerificationPayload(props, urlParam("iccid")),
    simActivationPayload: makeSelectSimActivationPayload(
      props,
      urlParam("iccid")
    ),
    thirdParty: makeSelectThirdPartySimVerification(props),
    minimumAge: makeSelectMinimumAge(),
    monthOptions: makeSelectMonthOptions(),
    dateOptions: makeSelectDateOptions(),
    yearOptions: makeSelectYearOptions(),
    iccIdDetails: makeSelectIccIdDetails(),
    eligiblePlans: makeSelectEligiblePlans(),
    appSettings: makeSelectAppSettings(),
    loading: makeSelectValidateIccidStatus(),
    numberType: makeSelectContractType(),
    order: makeSelectGuestOrder()
  });

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  onSubmit: (navigate, params) => {
    const useAPIGW =
      get(stateProps, "appSettings.API_GATEWAY_METHODS.validateIccId") || false;
    dispatchProps.validateIccId(
      { ...params, useAPIGW },
      ({ channel, order_paid, plan, iccid, status, required, order }) => {
        const order_ref = get(order, "order_ref");
        const currentChannel = getChannelFromUrl();
        const currentChannelVariants = get(
          stateProps,
          `appSettings.channel_variants.${currentChannel}`
        );
        const variantChannel = includes(currentChannelVariants, channel);
        const sameChannel = channel === currentChannel;
        const validChannel =
          sameChannel || variantChannel || channel === SELF_ACT_DEL;
        if (order_paid) {
          const redirectUrl =
            WEB_CHANNELS.includes(channel) || channel === SELF_ACT_DEL
              ? OLD_WEBFRONT_URL + paths.MANAGE_ORDER
              : paths.MANAGE_ORDER;
          const orderRefParams = order_ref
            ? {
                order_ref,
                order_reference_number: order_ref
              }
            : {};
          gotoUrl(redirectUrl, true, {
            ...orderRefParams,
            showActivate: true,
            redirect: STEP_ACTIONS.ACTIVATE_SIM_CARD,
            reset: true
          });
        } else if (validChannel && (status || iccid)) {
          // sim plan name takes higher priority than plan_name in query param
          const plan_name = get(plan, "name") || urlParam("plan_name");
          if (plan_name) {
            const selectedPlan = find(stateProps.eligiblePlans, {
              name: plan_name
            });
            if (!isEmpty(selectedPlan)) {
              dispatchProps.selectPlan(selectedPlan);
              setTimeout(navigate, 0);
            } else {
              dispatchProps.fetchPlan({ plan_name }, res => {
                !isEmpty(res) && navigate();
              });
            }
          } else {
            dispatchProps.fetchPlan({}, res => {
              !isEmpty(res) && navigate();
            });
          }
        }
      }
    );
  },
  activateSimCardAndNavigate: navigate => {
    const isPortIn =
      get(stateProps, "order.contractType") === CONTRACT_TYPES.PORT_IN;
    const activateMethod = isPortIn
      ? dispatchProps.initiatePortIn
      : dispatchProps.activateSimCard;

    activateMethod(stateProps.simActivationPayload, (_, error) => {
      if (isEmpty(error)) {
        navigate();
      }
    });
  },

  activateReplaceSimCardAndNavigate: navigate => {
    const isPortIn =
      get(stateProps, "order.contractType") === CONTRACT_TYPES.PORT_IN;
    const activateMethod = isPortIn
      ? dispatchProps.initiatePortIn
      : dispatchProps.activateReplaceSimCard;

    activateMethod(stateProps.simActivationPayload, (_, error) => {
      if (isEmpty(error)) {
        navigate();
      }
    });
  },
  activateSimAndNavigate: navigate => {
    dispatchProps.activateSimCard(
      stateProps.simActivationPayload,
      (_, error) => {
        if (isEmpty(error)) {
          navigate();
        }
      }
    );
  },
  activateReplaceSimAndNavigate: navigate => {
    dispatchProps.activateReplaceSimCard(
      stateProps.simActivationPayload,
      (_, error) => {
        if (isEmpty(error)) {
          navigate();
        }
      }
    );
  }
});

const mapDispatchToProps = dispatch => {
  return {
    resetValidateIccId: () => dispatch(clearData("validateIccId")),
    validateIccId: (params, callback) =>
      dispatch(validateIccId(params, callback)),
    resetIccIdError: () =>
      dispatch(setData({ key: "validateIccId", error: "" })),
    fetchPlan: (params, callback) => dispatch(fetchPlan(params, callback)),
    selectPlan: plan => dispatch(selectPlan(plan)),
    activateSimCard: (params, callback) =>
      dispatch(activateSimCard(params, callback)),
    initiatePortIn: (params, callback) =>
      dispatch(initiatePortIn(params, callback)),
    activateReplaceSimCard: (params, callback) =>
      dispatch(activateReplaceSimCard(params, callback)),
    setIccidParam: iccidparam =>
      dispatch(setData({ key: "iccIdInUrlParam", data: { iccidparam } }))
  };
};

const withConnect = connect(mapStateToProps, mapDispatchToProps, mergeProps);
export default compose(
  withConnect,
  reduxForm({
    form: forms.SIM_VERIFICATION_FORM,
    destroyOnUnmount: false,
    validate
  }),
  memo
)(SimVerificationHOC(SmartRender));
